import React, { Suspense, lazy, memo, useMemo } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { useThemeMode, ThemeModeProvider } from './contexts/ThemeContext';
import { COLORS } from './theme/colors';
import './i18n'; // 确保在应用启动时初始化i18n
import { Box, CircularProgress } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { drawerWidth } from './components/Sidebar';

// Lazy load page components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const InstallationList = lazy(() => import('./pages/InstallationList'));
const InstallationDetail = lazy(() => import('./pages/InstallationDetail'));
const InstallationSettings = lazy(() => import('./pages/InstallationSettings'));
const BatteryDetail = lazy(() => import('./pages/BatteryDetail'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const AdminUserView = lazy(() => import('./pages/AdminUserView'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Settings = lazy(() => import('./pages/Settings'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const Gallery = lazy(() => import('./pages/Gallery'));
const SupportList = lazy(() => import('./pages/SupportList'));
const SupportPost = lazy(() => import('./pages/SupportPost'));
const SupportNewPost = lazy(() => import('./pages/SupportNewPost'));
const SupportMyPosts = lazy(() => import('./pages/SupportMyPosts'));
const SupportAdmin = lazy(() => import('./pages/SupportAdmin'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const NotFound = lazy(() => import('./pages/NotFound'));



// 受保护路由组件
const ProtectedRoute = ({ roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

// 布局组件 - 用于包装应用主内容
const AppLayout = memo(() => {
  return (
    <>
      <Navbar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 0, sm: 1, md: 3, lg: 4 }, // 电脑端增加内边距
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: `${drawerWidth}px` },
          minHeight: 'calc(100vh - 64px)',
          marginTop: '64px', // 为顶部AppBar留出空间
        }}
      >
        <Outlet />
      </Box>
    </>
  );
});

// 无导航栏布局 - 用于登录和注册页面
const NoNavLayout = memo(() => {
  return <Outlet />;
});

// 主要应用内容
function AppContent() {
  const { user } = useAuth();
  const { effectiveTheme } = useThemeMode();

  // Create dynamic theme based on mode
  const theme = useMemo(() => createTheme({
    palette: {
      mode: effectiveTheme,
      primary: {
        main: COLORS.brand.orange.main,
        contrastText: effectiveTheme === 'dark' ? '#000000' : '#ffffff',
      },
      secondary: {
        main: COLORS.brand.blue.main,
      },
      ...(effectiveTheme === 'dark' ? {
        background: {
          default: '#121212',
          paper: COLORS.dark.background.paper,
        },
        text: {
          primary: COLORS.dark.text.primary,
          secondary: COLORS.dark.text.secondary,
        }
      } : {
        background: {
          default: '#ffffff',
          paper: '#ffffff',
        }
      })
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            // 仅在电脑尺寸下给 Paper 添加 padding，但排除 AppBar
            '&:not(.MuiAppBar-root)': {
              '@media (min-width: 600px)': {
                padding: '16px'
              }
            }
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: effectiveTheme === 'dark' ? {
          body: {
            scrollbarColor: '#6b6b6b #121212',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: '#121212',
              width: '12px',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: '#6b6b6b',
              minHeight: 24,
            },
          },
        } : {},
      },
    },
  }), [effectiveTheme]);

  // 根据用户角色重定向到相应的仪表盘
  const redirectToDashboard = () => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }

    if (user.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />;
    } else if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ScrollToTop />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            {/* 无导航栏路由（登录和注册） */}
            <Route element={<NoNavLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>

            {/* 带导航栏布局 */}
            <Route element={<AppLayout />}>
              {/* 公共路由 */}
              <Route path="/" element={redirectToDashboard()} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/support" element={<SupportList />} />
              <Route path="/support/new" element={<SupportNewPost />} />
              <Route path="/support/:postId/edit" element={<SupportNewPost />} />
              <Route path="/support/my-posts" element={<SupportMyPosts />} />
              <Route path="/support/admin" element={<SupportAdmin />} />
              <Route path="/support/:postId" element={<SupportPost />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />

              {/* 受保护路由 */}
              <Route element={<ProtectedRoute roles={['user', 'admin', 'superadmin']} />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/installations" element={<InstallationList />} />
                <Route path="/installations/:id" element={<InstallationDetail />} />
                <Route path="/installations/:id/settings" element={<InstallationSettings />} />
                <Route path="/installations/:id/battery/:index" element={<BatteryDetail />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:id" element={<NewsDetail />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/api-keys" element={<ApiKeys />} />
              </Route>

              {/* 管理员路由 */}
              <Route element={<ProtectedRoute roles={['admin', 'superadmin']} />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/users/:userId" element={<AdminUserView />} />
              </Route>

              {/* 超级管理员路由 */}
              <Route element={<ProtectedRoute roles={['superadmin']} />}>
                <Route path="/superadmin" element={<SuperAdminDashboard />} />
                <Route path="/superadmin/admins/:adminId" element={<AdminUserView />} />
                <Route path="/superadmin/users/:userId" element={<AdminUserView />} />
              </Route>
            </Route>

            {/* 404页面 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeModeProvider>
  );
}

export default App;
